import type { ReconcilePreview } from '@/api/permissions'

export type SaveGuard =
  | { kind: 'ok' }
  | { kind: 'warn'; message: string }
  | { kind: 'unknown'; message: string }

const plural = (n: number, s: string, p: string) => (n === 1 ? s : p)

/**
 * O que a tela faz com o `dry_run` do reconcile antes de gravar o perfil.
 *
 * Salvar um perfil dispara sync TOTAL do `user_tenants` de quem o tem como
 * perfil global (`api.py:8686`) — mesmo quando o save só mexeu numa restrição.
 * Só REMOÇÃO tranca usuário do lado de fora, então é ela que pede confirmação;
 * adição segue direto.
 *
 * `preview` nulo = a consulta falhou. Não vira "ok": o fusível existe para não
 * remover acesso em silêncio, e tratar falha como permissão manteria
 * exatamente o silêncio.
 */
export function reconcileSaveGuard(preview: ReconcilePreview | null): SaveGuard {
  if (!preview) {
    return {
      kind: 'unknown',
      message:
        'Não foi possível conferir o impacto no escopo dos usuários antes de salvar. '
        + 'Salvar reescreve o escopo de quem usa este perfil.',
    }
  }
  if (preview.removed <= 0) return { kind: 'ok' }
  const afetados = preview.changes.filter((c) => c.remove.length > 0).length
  return {
    kind: 'warn',
    message:
      `Salvar vai remover ${preview.removed} ${plural(preview.removed, 'acesso', 'acessos')} `
      + `de ${afetados} ${plural(afetados, 'usuário', 'usuários')}. `
      + 'O escopo de tenants é recalculado a partir do perfil e das organizações de cada um.',
  }
}
